<template>
  <div class="analysis-page">
    <main class="analysis-body">
      <div class="analysis-inner">

        <!-- HERO / UPLOAD AREA -->
        <div class="analysis-hero">
          <h1 class="analysis-title">Karar Analizi</h1>
          <p class="analysis-subtitle">
            Kararinizi yukleyin, yuksek mahkeme ictihatlariya karsilastiralim.
          </p>

          <!-- FILE DROP ZONE -->
          <div
            class="file-drop-zone"
            :class="{ 'is-dragover': isDragover, 'has-file': selectedFile }"
            @dragover.prevent="isDragover = true"
            @dragleave.prevent="isDragover = false"
            @drop.prevent="handleDrop"
            @click="triggerFileInput"
          >
            <input
              ref="fileInput"
              type="file"
              accept=".pdf,.docx,.doc"
              style="display: none"
              @change="handleFileSelect"
            />

            <template v-if="!selectedFile">
              <span class="material-symbols-outlined drop-icon">upload_file</span>
              <p class="drop-text">PDF veya DOCX dosyanizi surukleyin veya tiklayin</p>
              <p class="drop-hint">Maksimum 10MB</p>
            </template>

            <template v-else>
              <div class="file-info">
                <span class="material-symbols-outlined file-icon">description</span>
                <div class="file-details">
                  <span class="file-name">{{ selectedFile.name }}</span>
                  <span class="file-size">{{ formatFileSize(selectedFile.size) }}</span>
                </div>
                <button class="file-remove" @click.stop="removeFile" title="Dosyayi kaldir">
                  <span class="material-symbols-outlined">close</span>
                </button>
              </div>
            </template>
          </div>

          <!-- ANALYZE / STOP BUTTONS -->
          <div class="action-buttons">
            <button
              v-if="!loading"
              class="analyze-btn"
              :disabled="!selectedFile"
              @click="startAnalysis"
            >
              <span class="material-symbols-outlined">analytics</span>
              Analiz Et
            </button>
            <button
              v-else
              class="stop-btn"
              @click="stopAnalysis"
            >
              <span class="material-symbols-outlined">stop_circle</span>
              Durdur
            </button>
          </div>

          <!-- ERROR -->
          <div v-if="errorMessage" class="analysis-error">
            <span class="material-symbols-outlined">error</span>
            {{ errorMessage }}
          </div>
        </div>

        <!-- ANALYSIS RESULT -->
        <div v-if="analysisHtml || loading" class="analysis-result">
          <div class="result-header">
            <span class="material-symbols-outlined">fact_check</span>
            <h2>Analiz Raporu</h2>
            <span v-if="analyzedFileName" class="result-filename">{{ analyzedFileName }}</span>
            <button
              v-if="loading"
              class="stop-btn-header"
              @click="stopAnalysis"
              title="Analizi durdur (ESC)"
            >
              <span class="material-symbols-outlined">stop_circle</span>
              Durdur
            </button>
          </div>

          <!-- STREAMING / FINAL CONTENT -->
          <div class="result-content" ref="resultContent">
            <div v-if="loading && !analysisHtml" class="typing-indicator">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
            <div v-html="analysisHtml" class="legal-text"></div>
          </div>

          <!-- SOURCES -->
          <div v-if="sources.length > 0" class="result-sources">
            <h3>
              <span class="material-symbols-outlined">library_books</span>
              Referans Kaynaklar ({{ sources.length }})
            </h3>
            <div class="source-list">
              <div
                v-for="source in sources"
                :key="source.file_id"
                class="source-card"
              >
                <div class="source-index">[{{ source.index }}]</div>
                <div class="source-body">
                  <div class="source-name">{{ source.file_name }}</div>
                  <div v-if="source.quote" class="source-quote">
                    "{{ source.quote.substring(0, 200) }}{{ source.quote.length > 200 ? '...' : '' }}"
                  </div>
                </div>
                <div class="source-actions">
                  <button
                    class="source-btn"
                    @click="previewSource(source)"
                    title="Onizle"
                  >
                    <span class="material-symbols-outlined">visibility</span>
                  </button>
                  <button
                    class="source-btn"
                    @click="downloadSource(source)"
                    title="Indir"
                  >
                    <span class="material-symbols-outlined">download</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

      </div>
    </main>

    <!-- DOCUMENT PREVIEW MODAL -->
    <DocumentPreviewModal
      v-model="showDocPreview"
      :document="previewDocument"
      @close="showDocPreview = false"
    />
  </div>
</template>

<script>
import { marked } from "marked";
import DocumentPreviewModal from "../LawAssistant/DocumentPreviewModal.vue";

marked.setOptions({
  breaks: true,
  gfm: true,
});

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;

export default {
  name: "DecisionAnalysis",
  components: {
    DocumentPreviewModal,
  },
  data() {
    return {
      selectedFile: null,
      isDragover: false,
      loading: false,
      errorMessage: "",
      analysisHtml: "",
      streamingText: "",
      analyzedFileName: "",
      sources: [],
      showDocPreview: false,
      previewDocument: null,
      abortController: null,
    };
  },
  mounted() {
    this._onKeyDown = (e) => {
      if (e.key === "Escape" && this.loading) {
        this.stopAnalysis();
      }
    };
    document.addEventListener("keydown", this._onKeyDown);
  },
  beforeUnmount() {
    document.removeEventListener("keydown", this._onKeyDown);
    if (this.abortController) {
      this.abortController.abort();
    }
  },
  methods: {
    stopAnalysis() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      this.loading = false;
    },
    triggerFileInput() {
      if (!this.selectedFile) {
        this.$refs.fileInput.click();
      }
    },

    handleFileSelect(e) {
      const file = e.target.files[0];
      if (file) this.setFile(file);
    },

    handleDrop(e) {
      this.isDragover = false;
      const file = e.dataTransfer.files[0];
      if (file) this.setFile(file);
    },

    setFile(file) {
      const validTypes = [
        "application/pdf",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        "application/msword",
      ];
      const ext = file.name.split(".").pop().toLowerCase();

      if (!validTypes.includes(file.type) && !["pdf", "docx", "doc"].includes(ext)) {
        this.errorMessage = "Sadece PDF ve DOCX dosyalari desteklenmektedir.";
        return;
      }

      if (file.size > 10 * 1024 * 1024) {
        this.errorMessage = "Dosya boyutu 10MB'i gecemez.";
        return;
      }

      this.selectedFile = file;
      this.errorMessage = "";
    },

    removeFile() {
      this.selectedFile = null;
      this.$refs.fileInput.value = "";
      this.errorMessage = "";
    },

    formatFileSize(bytes) {
      if (bytes < 1024) return bytes + " B";
      if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + " KB";
      return (bytes / (1024 * 1024)).toFixed(1) + " MB";
    },

    formatLegalText(html) {
      if (!html) return html;
      // Yasa isimleri
      html = html.replace(
        /(\d+\/\d+\s+sayılı\s+[^<.]+(?:Kanunu?|Yasası|Tüzüğü))/gi,
        '<strong>$1</strong>'
      );
      // Madde numaralari
      html = html.replace(
        /\b(Madde\s+\d+(?:\/\d+)?)\b/g,
        '<strong>$1</strong>'
      );
      return html;
    },

    async startAnalysis() {
      if (!this.selectedFile || this.loading) return;

      const token = localStorage.getItem("auth_token");
      if (!token) {
        this.errorMessage = "Oturum suresi dolmus. Lutfen tekrar giris yapin.";
        return;
      }

      this.loading = true;
      this.errorMessage = "";
      this.analysisHtml = "";
      this.streamingText = "";
      this.sources = [];
      this.analyzedFileName = this.selectedFile.name;

      const formData = new FormData();
      formData.append("file", this.selectedFile);

      this.abortController = new AbortController();

      try {
        const response = await fetch(`${API_BASE_URL}/analysis/upload`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "text/event-stream",
          },
          body: formData,
          signal: this.abortController.signal,
        });

        if (!response.ok) {
          const errData = await response.json().catch(() => null);
          throw new Error(errData?.error || `HTTP hata: ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        let done = false;

        while (!done) {
          const result = await reader.read();
          done = result.done;
          if (done) break;

          buffer += decoder.decode(result.value, { stream: true });

          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (!line.startsWith("data: ")) continue;

            const jsonStr = line.slice(6).trim();
            if (jsonStr === "[DONE]") continue;

            try {
              const data = JSON.parse(jsonStr);

              if (data.error) {
                this.errorMessage = data.error;
                this.loading = false;
                return;
              }

              if (data.type === "delta" && data.text) {
                this.streamingText += data.text;
                let formattedHtml = marked.parse(this.streamingText);
                formattedHtml = this.formatLegalText(formattedHtml);
                this.analysisHtml = formattedHtml;

                this.$nextTick(() => {
                  const el = this.$refs.resultContent;
                  if (el) el.scrollTop = el.scrollHeight;
                });
              }

              if (data.type === "complete") {
                let finalHtml = marked.parse(this.streamingText);
                finalHtml = this.formatLegalText(finalHtml);
                this.analysisHtml = finalHtml;

                if (data.sources && Array.isArray(data.sources)) {
                  this.sources = data.sources;
                }

                this.loading = false;
              }
            } catch (parseErr) {
              // JSON parse hatasi - devam et
            }
          }
        }

        // Stream bitti ama complete gelmemisse
        if (this.loading) {
          this.loading = false;
          if (!this.analysisHtml) {
            this.errorMessage = "Analiz tamamlanamadi. Lutfen tekrar deneyin.";
          }
        }

      } catch (err) {
        if (err.name === "AbortError") {
          // Kullanici tarafindan durduruldu
          return;
        }
        this.errorMessage = err.message || "Analiz sirasinda bir hata olustu.";
        this.loading = false;
      } finally {
        this.abortController = null;
      }
    },

    previewSource(source) {
      this.previewDocument = source;
      this.showDocPreview = true;
    },

    async downloadSource(source) {
      const token = localStorage.getItem("auth_token");
      if (!token) return;

      try {
        const response = await fetch(
          `${API_BASE_URL}/files/${source.file_id}/download?filename=${encodeURIComponent(source.file_name)}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        if (!response.ok) {
          this.errorMessage = "Dosya indirilemedi. Dosya yerel sunucuda bulunamadi.";
          return;
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = source.file_name;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch {
        this.errorMessage = "Dosya indirilirken bir hata olustu.";
      }
    },
  },
};
</script>

<style scoped>
.analysis-page {
  display: flex;
  flex-direction: column;
  height: 100vh;
  background: #f8faf9;
  font-family: 'Inter', 'Segoe UI', sans-serif;
}

.analysis-body {
  flex: 1;
  overflow-y: auto;
  padding: 40px 20px;
}

.analysis-inner {
  max-width: 800px;
  margin: 0 auto;
}

/* HERO */
.analysis-hero {
  text-align: center;
  margin-bottom: 32px;
}

.analysis-title {
  font-size: 28px;
  font-weight: 700;
  color: #1a5f4a;
  margin-bottom: 8px;
}

.analysis-subtitle {
  font-size: 15px;
  color: #6b7280;
  margin-bottom: 28px;
}

/* FILE DROP ZONE */
.file-drop-zone {
  border: 2px dashed #c5d6cf;
  border-radius: 16px;
  padding: 40px 24px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: #fff;
  margin-bottom: 20px;
}

.file-drop-zone:hover,
.file-drop-zone.is-dragover {
  border-color: #1a5f4a;
  background: #f0f7f4;
}

.file-drop-zone.has-file {
  border-style: solid;
  border-color: #1a5f4a;
  cursor: default;
  padding: 20px 24px;
}

.drop-icon {
  font-size: 48px;
  color: #1a5f4a;
  opacity: 0.6;
  display: block;
  margin-bottom: 12px;
}

.drop-text {
  font-size: 15px;
  color: #374151;
  margin-bottom: 4px;
}

.drop-hint {
  font-size: 13px;
  color: #9ca3af;
}

/* FILE INFO */
.file-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.file-icon {
  font-size: 36px;
  color: #1a5f4a;
}

.file-details {
  flex: 1;
  text-align: left;
}

.file-name {
  display: block;
  font-weight: 600;
  color: #1f2937;
  font-size: 14px;
}

.file-size {
  font-size: 12px;
  color: #9ca3af;
}

.file-remove {
  background: none;
  border: none;
  cursor: pointer;
  color: #9ca3af;
  padding: 4px;
  border-radius: 50%;
  transition: all 0.15s;
}

.file-remove:hover {
  color: #ef4444;
  background: #fef2f2;
}

/* ACTION BUTTONS */
.action-buttons {
  margin-bottom: 4px;
}

.analyze-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 32px;
  background: #1a5f4a;
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.analyze-btn:hover:not(:disabled) {
  background: #155240;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(26, 95, 74, 0.3);
}

.analyze-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.stop-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 32px;
  background: #dc2626;
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.stop-btn:hover {
  background: #b91c1c;
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

.stop-btn-header {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 16px;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.stop-btn-header:hover {
  background: #dc2626;
  border-color: #dc2626;
}

.stop-btn-header .material-symbols-outlined {
  font-size: 18px;
}

.btn-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255,255,255,0.3);
  border-top-color: #fff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* ERROR */
.analysis-error {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 16px;
  padding: 12px 16px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  color: #991b1b;
  font-size: 14px;
}

.analysis-error .material-symbols-outlined {
  font-size: 20px;
  color: #ef4444;
}

/* RESULT */
.analysis-result {
  background: #fff;
  border-radius: 16px;
  border: 1px solid #e5e7eb;
  overflow: hidden;
  margin-top: 24px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.06);
}

.result-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 16px 24px;
  background: linear-gradient(135deg, #1a5f4a 0%, #2d8a6e 100%);
  color: #fff;
}

.result-header .material-symbols-outlined {
  font-size: 24px;
}

.result-header h2 {
  font-size: 17px;
  font-weight: 600;
  margin: 0;
  flex: 1;
}

.result-filename {
  font-size: 12px;
  background: rgba(255,255,255,0.2);
  padding: 3px 10px;
  border-radius: 20px;
}

.result-content {
  padding: 24px;
  max-height: 600px;
  overflow-y: auto;
  line-height: 1.7;
  font-size: 14px;
  color: #1f2937;
}

.result-content :deep(h1),
.result-content :deep(h2),
.result-content :deep(h3) {
  color: #1a5f4a;
  margin-top: 20px;
  margin-bottom: 10px;
}

.result-content :deep(strong) {
  color: #1a5f4a;
}

.result-content :deep(ul),
.result-content :deep(ol) {
  padding-left: 20px;
}

.result-content :deep(li) {
  margin-bottom: 6px;
}

.result-content :deep(blockquote) {
  border-left: 3px solid #c9a227;
  padding-left: 16px;
  margin: 12px 0;
  color: #4b5563;
  font-style: italic;
}

/* TYPING INDICATOR */
.typing-indicator {
  display: flex;
  gap: 4px;
  padding: 8px 0;
}

.typing-indicator .dot {
  width: 8px;
  height: 8px;
  background: #1a5f4a;
  border-radius: 50%;
  animation: bounce 1.4s infinite ease-in-out both;
}

.typing-indicator .dot:nth-child(1) { animation-delay: -0.32s; }
.typing-indicator .dot:nth-child(2) { animation-delay: -0.16s; }

@keyframes bounce {
  0%, 80%, 100% { transform: scale(0); }
  40% { transform: scale(1); }
}

/* SOURCES */
.result-sources {
  border-top: 1px solid #e5e7eb;
  padding: 20px 24px;
  background: #f9fafb;
}

.result-sources h3 {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
  color: #1a5f4a;
  margin: 0 0 14px 0;
}

.result-sources h3 .material-symbols-outlined {
  font-size: 20px;
}

.source-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.source-card {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 12px 14px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  transition: border-color 0.15s;
}

.source-card:hover {
  border-color: #1a5f4a;
}

.source-index {
  font-weight: 700;
  color: #1a5f4a;
  font-size: 13px;
  min-width: 28px;
  padding-top: 2px;
}

.source-body {
  flex: 1;
  min-width: 0;
}

.source-name {
  font-weight: 600;
  font-size: 13px;
  color: #1f2937;
  margin-bottom: 4px;
  word-break: break-word;
}

.source-quote {
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
  font-style: italic;
}

.source-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.source-btn {
  background: none;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 6px;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.15s;
}

.source-btn:hover {
  color: #1a5f4a;
  border-color: #1a5f4a;
  background: #f0f7f4;
}

.source-btn .material-symbols-outlined {
  font-size: 18px;
  display: block;
}

/* RESPONSIVE */
@media (max-width: 768px) {
  .analysis-body {
    padding: 20px 12px;
  }

  .analysis-title {
    font-size: 22px;
  }

  .file-drop-zone {
    padding: 28px 16px;
  }

  .result-content {
    padding: 16px;
    max-height: 400px;
  }
}
</style>
